import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  FileText,
  Download,
  FileSpreadsheet,
  Filter,
  Calendar,
  Users,
  TrendingUp,
  BarChart3,
  PieChart,
  Target,
  Award,
  AlertCircle,
  CheckCircle,
  Eye
} from 'lucide-react';
import { 
  VictoryChart, 
  VictoryBar, 
  VictoryAxis, 
  VictoryTheme,
  VictoryPie,
  VictoryLabel,
  VictoryLine,
  VictoryLegend,
  VictoryTooltip,
  VictoryVoronoiContainer
} from 'victory';

const API = process.env.REACT_APP_BACKEND_URL;

const Relatorios180 = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  
  // Filtros
  const [filtroCiclo, setFiltroCiclo] = useState('');
  const [filtroArea, setFiltroArea] = useState('');
  const [filtroPeriodo, setFiltroPeriodo] = useState('todos');
  
  // Dados
  const [ciclos, setCiclos] = useState([]);
  const [relatorioData, setRelatorioData] = useState(null);
  const [estatisticas, setEstatisticas] = useState(null);

  useEffect(() => {
    carregarCiclos();
  }, []);

  useEffect(() => {
    if (filtroCiclo) {
      carregarDadosRelatorio();
    }
  }, [filtroCiclo, filtroArea, filtroPeriodo]);

  const carregarCiclos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback180/ciclos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCiclos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar ciclos:', error);
    }
  };

  const carregarDadosRelatorio = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const params = { ciclo_id: filtroCiclo };
      if (filtroArea) params.area = filtroArea;
      
      const response = await axios.get(`${API}/api/feedback180/relatorios/completo`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setRelatorioData(response.data);
      calcularEstatisticas(response.data);
      
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar dados do relatório',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const calcularEstatisticas = (data) => {
    if (!data) return;
    
    const stats = {
      totalAvaliacoes: data.avaliacoes?.length || 0,
      mediaGeral: 0,
      taxaParticipacao: 0,
      melhorCompetencia: null,
      competenciaMelhorar: null
    };
    
    if (data.avaliacoes && data.avaliacoes.length > 0) {
      const somaNotas = data.avaliacoes.reduce((sum, av) => sum + (av.media_geral || 0), 0);
      stats.mediaGeral = (somaNotas / data.avaliacoes.length).toFixed(1);
      
      const totalEsperado = data.total_participantes || data.avaliacoes.length;
      stats.taxaParticipacao = ((data.avaliacoes.length / totalEsperado) * 100).toFixed(0);
    }
    
    if (data.competencias && data.competencias.length > 0) {
      const ordenadas = [...data.competencias].sort((a, b) => b.media - a.media);
      stats.melhorCompetencia = ordenadas[0];
      stats.competenciaMelhorar = ordenadas[ordenadas.length - 1];
    }
    
    setEstatisticas(stats);
  };

  const gerarPDF = async () => {
    setLoadingPDF(true);
    try {
      const token = localStorage.getItem('token');
      
      const params = { ciclo_id: filtroCiclo };
      if (filtroArea) params.area = filtroArea;
      
      const response = await axios.get(`${API}/api/feedback180/relatorios/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });
      
      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_feedback_180_${filtroCiclo}_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: 'Sucesso',
        description: 'Relatório PDF gerado com sucesso',
      });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao gerar relatório PDF',
        variant: 'destructive'
      });
    } finally {
      setLoadingPDF(false);
    }
  };

  const exportarExcel = async () => {
    setLoadingExcel(true);
    try {
      const token = localStorage.getItem('token');
      
      const params = { ciclo_id: filtroCiclo };
      if (filtroArea) params.area = filtroArea;
      
      const response = await axios.get(`${API}/api/feedback180/relatorios/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });
      
      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_feedback_180_${filtroCiclo}_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: 'Sucesso',
        description: 'Relatório Excel exportado com sucesso',
      });
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao exportar relatório Excel',
        variant: 'destructive'
      });
    } finally {
      setLoadingExcel(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="h-8 w-8 mr-3 text-blue-600" />
            Relatórios Visuais - Feedback 180º
          </h1>
          <p className="text-gray-600 mt-1">
            Relatórios completos com gráficos e exportação em PDF/Excel
          </p>
        </div>
      </div>

      {/* Filtros e Ações */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filtros e Exportação
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ciclo de Avaliação *
              </label>
              <select
                value={filtroCiclo}
                onChange={(e) => setFiltroCiclo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              >
                <option value="">Selecione um ciclo</option>
                {ciclos.map(ciclo => (
                  <option key={ciclo.id} value={ciclo.id}>
                    {ciclo.nome} ({ciclo.status})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Área
              </label>
              <select
                value={filtroArea}
                onChange={(e) => setFiltroArea(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todas as áreas</option>
                <option value="Tecnologia">Tecnologia</option>
                <option value="Vendas">Vendas</option>
                <option value="Marketing">Marketing</option>
                <option value="RH">Recursos Humanos</option>
                <option value="Financeiro">Financeiro</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Período
              </label>
              <select
                value={filtroPeriodo}
                onChange={(e) => setFiltroPeriodo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="todos">Todos</option>
                <option value="30dias">Últimos 30 dias</option>
                <option value="60dias">Últimos 60 dias</option>
                <option value="90dias">Últimos 90 dias</option>
              </select>
            </div>
            
            <div className="flex items-end space-x-2">
              <Button
                onClick={gerarPDF}
                disabled={!filtroCiclo || loadingPDF}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                <FileText className={`h-4 w-4 mr-2 ${loadingPDF ? 'animate-pulse' : ''}`} />
                PDF
              </Button>
              
              <Button
                onClick={exportarExcel}
                disabled={!filtroCiclo || loadingExcel}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <FileSpreadsheet className={`h-4 w-4 mr-2 ${loadingExcel ? 'animate-pulse' : ''}`} />
                Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estatísticas Gerais */}
      {estatisticas && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total de Avaliações</p>
                  <p className="text-3xl font-bold text-blue-600">{estatisticas.totalAvaliacoes}</p>
                </div>
                <Users className="h-10 w-10 text-blue-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Média Geral</p>
                  <p className="text-3xl font-bold text-green-600">{estatisticas.mediaGeral}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Taxa de Participação</p>
                  <p className="text-3xl font-bold text-purple-600">{estatisticas.taxaParticipacao}%</p>
                </div>
                <CheckCircle className="h-10 w-10 text-purple-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">PDIs Gerados</p>
                  <p className="text-3xl font-bold text-orange-600">{relatorioData?.pdis_gerados || 0}</p>
                </div>
                <Target className="h-10 w-10 text-orange-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Gráficos */}
      {relatorioData && (
        <>
          {/* Gráfico de Competências */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Avaliação por Competências
              </CardTitle>
              <CardDescription>
                Média de avaliação de cada competência
              </CardDescription>
            </CardHeader>
            <CardContent>
              {relatorioData.competencias && relatorioData.competencias.length > 0 ? (
                <VictoryChart
                  theme={VictoryTheme.material}
                  domainPadding={20}
                  height={300}
                  containerComponent={<VictoryVoronoiContainer />}
                >
                  <VictoryAxis
                    tickValues={relatorioData.competencias.map((_, i) => i + 1)}
                    tickFormat={relatorioData.competencias.map(c => c.nome.substring(0, 15))}
                    style={{
                      tickLabels: { fontSize: 10, angle: -45, textAnchor: 'end' }
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    tickFormat={(x) => `${x}`}
                  />
                  <VictoryBar
                    data={relatorioData.competencias.map((comp, i) => ({
                      x: i + 1,
                      y: comp.media,
                      label: `${comp.nome}: ${comp.media.toFixed(1)}`
                    }))}
                    style={{
                      data: { 
                        fill: ({ datum }) => datum.y >= 4 ? '#10b981' : datum.y >= 3 ? '#f59e0b' : '#ef4444'
                      }
                    }}
                    labelComponent={<VictoryTooltip />}
                  />
                </VictoryChart>
              ) : (
                <p className="text-center text-gray-500 py-8">Nenhum dado de competências disponível</p>
              )}
            </CardContent>
          </Card>

          {/* Distribuição de Notas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <PieChart className="h-5 w-5 mr-2" />
                Distribuição de Notas
              </CardTitle>
              <CardDescription>
                Percentual de avaliações por faixa de nota
              </CardDescription>
            </CardHeader>
            <CardContent>
              {relatorioData.distribuicao_notas ? (
                <div className="flex justify-center">
                  <VictoryPie
                    data={[
                      { x: 'Excelente (4.5-5.0)', y: relatorioData.distribuicao_notas.excelente || 0 },
                      { x: 'Bom (4.0-4.4)', y: relatorioData.distribuicao_notas.bom || 0 },
                      { x: 'Regular (3.0-3.9)', y: relatorioData.distribuicao_notas.regular || 0 },
                      { x: 'Insatisfatório (<3.0)', y: relatorioData.distribuicao_notas.insatisfatorio || 0 }
                    ]}
                    colorScale={['#10b981', '#3b82f6', '#f59e0b', '#ef4444']}
                    labelRadius={80}
                    style={{ 
                      labels: { fontSize: 12, fill: 'white', fontWeight: 'bold' } 
                    }}
                    height={300}
                  />
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Nenhum dado de distribuição disponível</p>
              )}
            </CardContent>
          </Card>

          {/* Melhores e Piores Competências */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {estatisticas?.melhorCompetencia && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="flex items-center text-green-700">
                    <Award className="h-5 w-5 mr-2" />
                    Melhor Competência
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg font-semibold text-green-900">
                    {estatisticas.melhorCompetencia.nome}
                  </p>
                  <div className="mt-2 flex items-center">
                    <span className="text-2xl font-bold text-green-600">
                      {estatisticas.melhorCompetencia.media.toFixed(1)}
                    </span>
                    <span className="text-sm text-green-700 ml-2">/ 5.0</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {estatisticas?.competenciaMelhorar && (
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center text-orange-700">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Competência a Desenvolver
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg font-semibold text-orange-900">
                    {estatisticas.competenciaMelhorar.nome}
                  </p>
                  <div className="mt-2 flex items-center">
                    <span className="text-2xl font-bold text-orange-600">
                      {estatisticas.competenciaMelhorar.media.toFixed(1)}
                    </span>
                    <span className="text-sm text-orange-700 ml-2">/ 5.0</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* Estado vazio */}
      {!relatorioData && !loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <BarChart3 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Selecione um ciclo para visualizar os relatórios</p>
              <p className="text-sm text-gray-500 mt-2">
                Os gráficos e dados serão exibidos aqui após a seleção
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Carregando dados do relatório...</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Relatorios180;
